import { ZfFormField, ZfFormFieldAlert, ZfFormFieldLabel, ZfInput, ZfSelectOption } from '@zafire/react';
import { useFormikContext } from 'formik';
import { LookupEntity } from 'pco-product-service-internal-consumer';
import { PortfolioForm } from '../AddPortfolioDialog';
import { FieldInfoTooltip } from './FieldInfoTooltip';
import { FieldLoadingSpinner } from './FieldLoadingSpinner';
import { StringFieldName } from './portfolioFormFieldName';

export type SelectFieldOption = { value: string; label: string; disabled?: boolean };

export const toLookupOptions = (entities: readonly LookupEntity[]): SelectFieldOption[] =>
  entities.map((entity) => ({ value: entity.key, label: entity.value.label ?? entity.key }));

type FormSelectFieldProps = {
  name: StringFieldName;
  label: string;
  options: readonly SelectFieldOption[];
  mandatory?: boolean;
  tooltip?: string;
  isLoading?: boolean;
  readonly?: boolean;
  placeholder?: string;
  allowSearch?: boolean;
  onChange?: (value: string) => void;
};

export const FormSelectField = ({
  name,
  label,
  options,
  mandatory,
  tooltip,
  isLoading = false,
  readonly = false,
  placeholder,
  allowSearch = true,
  onChange,
}: FormSelectFieldProps) => {
  const { values, touched, errors, setFieldValue } = useFormikContext<PortfolioForm>();

  return (
    <ZfFormField style={{ flex: 1, minWidth: 0 }}>
      <ZfFormFieldLabel slot="label" htmlFor={name} text={label} mandatory={mandatory}>
        {tooltip && <FieldInfoTooltip fieldName={name} text={tooltip} />}
      </ZfFormFieldLabel>
      <ZfInput
        name={name}
        type="select"
        id={name}
        value={values[name]}
        readonly={readonly}
        placeholder={placeholder}
        allowSearch={allowSearch}
        error={!!errors[name] && !!touched[name]}
        onSelectChange={(e) => {
          const selected = String(e.detail);
          setFieldValue(name, selected);
          onChange?.(selected);
        }}
        getOptionLabel={(option) => options.find((item) => item.value === option)?.label || option}
      >
        {isLoading && <FieldLoadingSpinner />}
        {options.map((item) => (
          <ZfSelectOption
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            isSelected={values[name] === item.value}
          >
            {item.label}
          </ZfSelectOption>
        ))}
      </ZfInput>
      <ZfFormFieldAlert slot="alert" id={name} alerts={[errors[name]?.toString() ?? '']} />
    </ZfFormField>
  );
};
